import { useState } from 'react';

import Image from 'mui-image';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { isLoading } from 'hooks/requests';
import { snackAlertData } from 'utils/constants';
import { useThemeToggle } from '../hooks/useThemeToggle';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthService } from 'store/services/auth.service';
import { TextField, Button, SnackBarAlert } from '../shared';
import { useCustomMediaQuery } from '../hooks/useMediaQuery';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import CirclerImage from '../assets/icons/CirclerLeftImage.svg';
import { ReactComponent as DarkCirclerLogo } from '../assets/icons/DarkLogo.svg';
import { ReactComponent as CirclerLogo } from '../assets/icons/circler-logo.svg';

const styles = {
    container: {
        height: '100vh',
        overflow: 'hidden',
    },
    mainHeading: {
        fontSize: '32px',
        fontWeight: '700',
        color: (theme) => theme.textColor.dark,
        lineHeight: '38px',
    },
    subHeading: {
        fontSize: '18px',
        fontWeight: '500',
        color: '#6C727F',
        lineHeight: '24px',
        mt: 0.5,
    },

    forgotPassword: {
        color: (theme) => (theme.palette.mode === 'dark' ? '#3381E2' : '#0061DB'),
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 500,
    },
    loginBtnStyle: {
        borderRadius: '8px',
        fontSize: '18px',
        fontWeight: 500,
    },
    otherSignInOptionsContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    otherSignInOptionsText: {
        fontSize: '16px',
        fontWeight: '500',
        lineHeight: '24px',
        color: (theme) => (theme.palette.mode === 'dark' ? '#9EA3AE' : '#6C727F'),
    },
    signInOption: {
        color: '#FFF',
        fontSize: '16px',
        fontWeight: '500',
    },
    googleSignInOption: {
        color: (theme) => theme.textColor.dark,
        fontSize: '16px',
        fontWeight: '500',
    },
    otherSignInOption: {
        color: (theme) => theme.textColor.dark,
        fontSize: '16px',
        fontWeight: '400',
    },
    link: {
        textDecoration: 'none',
        color: (theme) => (theme.palette.mode === 'dark' ? '#3381E2' : '#0061DB'),
        // color: '#004EAF', // Blue color
        cursor: 'pointer', // Optional: Change cursor on hover,
        fontWeight: 600,
        fontSize: '16px',
    },
};

const ConfirmNewPassword = () => {
    const navigate = useNavigate();
    const { uid, token } = useParams();
    const { themeMode } = useThemeToggle();
    const { setNewPassword } = useAuthService();
    const isScreenLowerThan1024 = useCustomMediaQuery('(max-width: 1025px)');

    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [alertData, setAlertData] = useState(snackAlertData);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const handleSubmit = async () => {
        try {
            await setNewPassword(uid, token, password);
            handleSuccessAlert(setAlertData, 'Password has been reset successfully.');
            await new Promise((res) => setTimeout(res, 10));
            setTimeout(() => {
                navigate('/login');
            }, 1000);
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail || 'Something went wrong, Please try again';
            setError(errorMessage);
        }
    };

    return (
        <Grid flex={{ xs: '1', sm: '2', md: '3' }} container sx={styles.container}>
            <Grid
                item
                position="relative"
                xs={12}
                md={6}
                lg={6}
                maxWidth={'700px'}
                display={{ xs: 'none', md: 'block', lg: 'block' }}
                overflow={{ xs: 'scroll', md: 'hidden', lg: 'hidden' }}
            >
                <Image alt="Left Content" src={CirclerImage} />
            </Grid>
            <Grid
                container
                item
                xs={12}
                md={6}
                lg={6}
                flex="1"
                alignContent="flex-start"
                justifyContent={'center'}
                px={{ xs: 2, sm: 4, md: 8 }}
                pt="50px"
                sx={{
                    // height: 'calc(100vh - 25px)',
                    overflow: 'auto',
                    backgroundColor: (theme) => theme.backgroundColor.primary,
                }}
            >
                <Grid
                    item
                    container
                    direction="column"
                    maxWidth={'700px'}
                    padding={isScreenLowerThan1024 ? '24px 0' : '0 10%'}
                    gap={3}
                    textAlign={'start'}
                >
                    <Grid item>
                        {themeMode === 'dark' ? (
                            <DarkCirclerLogo width="180.995px" height="45.22px" />
                        ) : (
                            <CirclerLogo width="180.995px" height="45.22px" />
                        )}
                    </Grid>
                    <Grid item>
                        <Typography sx={styles.mainHeading}>Reset your password</Typography>
                        <Typography sx={styles.subHeading}>
                            Please enter your new password.
                        </Typography>
                    </Grid>

                    <Grid item>
                        <TextField
                            name="password"
                            label="Password"
                            placeholder="Password *"
                            value={password}
                            type={passwordVisible ? 'text' : 'password'}
                            onChange={(e) => {
                                setPassword(e.target.value);
                            }}
                            InputProps={{
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit();
                                    }
                                },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setPasswordVisible(!passwordVisible)}
                                            edge="end"
                                        >
                                            {passwordVisible ? (
                                                <VisibilityOutlinedIcon fontSize="small" />
                                            ) : (
                                                <VisibilityOffOutlinedIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {error && (
                        <Grid item>
                            <Typography
                                sx={{
                                    color: 'red',
                                    fontSize: 16,
                                    fontWeight: 500,
                                }}
                            >
                                {error}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Button
                            text={<Typography sx={styles.signInOption}>Submit</Typography>}
                            fullWidth
                            customStyles={styles.loginBtnStyle}
                            onClick={handleSubmit}
                            loading={isLoading()}
                        />
                    </Grid>

                    <Grid
                        container
                        item
                        justifyContent="space-between"
                        sx={styles.otherSignInOptionsContainer}
                    ></Grid>
                </Grid>
            </Grid>
            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Grid>
    );
};

export default ConfirmNewPassword;
